/*
 * Copyright (c) 2026
 */

const VERTEX_SHADER = `
attribute vec2 aPosition;
attribute vec2 aUv;
varying vec2 vUv;
void main() {
  vUv = aUv;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

const FRAGMENT_SHADER = `
precision mediump float;

uniform sampler2D uNoise;
uniform vec2 uWarpScroll;
uniform vec2 uLayerScroll1;
uniform vec2 uLayerScroll2;
uniform vec2 uLayerScroll3;
uniform float uPulse;
uniform vec2 uResolution;

varying vec2 vUv;

void main() {
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 uv = vec2(vUv.x * aspect, vUv.y);

  vec2 warpUv = uv * 0.22 + uWarpScroll;
  float warpX = texture2D(uNoise, warpUv).r;
  float warpY = texture2D(uNoise, warpUv + 0.37).g;
  vec2 warp = (vec2(warpX, warpY) - 0.5) * 0.4;

  float layer1 = texture2D(uNoise, uv * 0.30 + warp + uLayerScroll1).b;
  float layer2 = texture2D(uNoise, uv * 0.52 + warp * 1.25 + uLayerScroll2 + 0.17).r;
  float layer3 = texture2D(uNoise, uv * 0.85 + warp * 0.55 + uLayerScroll3 + 0.61).g;

  float fog = layer1 * 0.48 + layer2 * 0.34 + layer3 * 0.18;
  float wisp = smoothstep(0.42, 0.58, fog);
  float alpha = wisp * 0.22 * uPulse;

  vec3 color = vec3(0.84, 0.82, 0.78);
  gl_FragColor = vec4(color * alpha, alpha);
}
`

/** Fog is soft, so the drawing buffer stays well below the CSS size. */
const MAX_LONG_EDGE = 720
const NOISE_SIZE = 128
const FRAME_INTERVAL_MS = 1000 / 30
const MAX_FRAME_SECONDS = 0.1

/** Base drift, in noise-texture repeats per second. */
const DRIFT_X = 0.045
const DRIFT_Y = 0.06
const DRIFT_Z = 0.02

/**
 * Each axis scrolls on its own and wraps by a full texture repeat.
 * Scaling a shared wrapped offset makes the other axis jump by a fraction of a repeat.
 */
const WARP_SPEED_X = DRIFT_Z
const WARP_SPEED_Y = DRIFT_Z * 0.35
const LAYER1_SPEED_X = DRIFT_X
const LAYER1_SPEED_Y = DRIFT_X * 0.15
const LAYER2_SPEED_X = DRIFT_Y
const LAYER2_SPEED_Y = -DRIFT_Y * 0.22
const LAYER3_SPEED_X = DRIFT_Z * 0.7
const LAYER3_SPEED_Y = DRIFT_X * 0.4

const GL_ATTRIBUTES: WebGLContextAttributes = {
  alpha: true,
  antialias: false,
  depth: false,
  stencil: false,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  powerPreference: 'low-power',
}

function wrapUnit(value: number): number {
  const wrapped = value % 1
  return wrapped < 0 ? wrapped + 1 : wrapped
}

function advanceScroll(current: number, delta: number, speed: number): number {
  return wrapUnit(current + delta * speed)
}

function fade(t: number): number {
  return t * t * (3 - 2 * t)
}

function hash2(ix: number, iy: number, seed: number): number {
  const n = Math.sin(ix * 127.1 + iy * 311.7 + seed * 74.7) * 43_758.5453
  return n - Math.floor(n)
}

function tiledNoise(u: number, v: number, cells: number, seed: number): number {
  const x = u * cells
  const y = v * cells
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const tx = fade(x - x0)
  const ty = fade(y - y0)
  const wrap = (n: number) => ((n % cells) + cells) % cells
  const v00 = hash2(wrap(x0), wrap(y0), seed)
  const v10 = hash2(wrap(x0 + 1), wrap(y0), seed)
  const v01 = hash2(wrap(x0), wrap(y0 + 1), seed)
  const v11 = hash2(wrap(x0 + 1), wrap(y0 + 1), seed)
  return v00 + (v10 - v00) * tx + (v01 - v00) * ty + (v00 - v10 - v01 + v11) * tx * ty
}

function fbm(u: number, v: number, seed: number): number {
  const low = tiledNoise(u, v, 3, seed)
  const mid = tiledNoise(u, v, 6, seed + 17)
  const high = tiledNoise(u, v, 12, seed + 49)
  return low * 0.55 + mid * 0.3 + high * 0.15
}

function createNoiseData(): Uint8Array {
  const data = new Uint8Array(NOISE_SIZE * NOISE_SIZE * 4)
  for (let y = 0; y < NOISE_SIZE; y++) {
    for (let x = 0; x < NOISE_SIZE; x++) {
      const u = x / NOISE_SIZE
      const v = y / NOISE_SIZE
      const index = (y * NOISE_SIZE + x) * 4
      data[index] = Math.round(fbm(u, v, 1) * 255)
      data[index + 1] = Math.round(fbm(u, v, 2) * 255)
      data[index + 2] = Math.round(fbm(u, v, 3) * 255)
      data[index + 3] = 255
    }
  }
  return data
}

function backingSize(cssWidth: number, cssHeight: number): { width: number; height: number } {
  const longEdge = Math.max(cssWidth, cssHeight)
  const scale = longEdge > MAX_LONG_EDGE ? MAX_LONG_EDGE / longEdge : 1
  return {
    width: Math.max(1, Math.round(cssWidth * scale)),
    height: Math.max(1, Math.round(cssHeight * scale)),
  }
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) {
    return null
  }
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

/**
 * Draws drifting fog into `canvas`. Returns a cleanup function.
 * No-ops when WebGL is unavailable.
 */
export function attachFogEffect(canvas: HTMLCanvasElement): () => void {
  const gl = canvas.getContext('webgl', GL_ATTRIBUTES)
  if (!gl) {
    return () => undefined
  }

  const bindProgram = gl.useProgram.bind(gl)

  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
  const program = gl.createProgram()
  if (!(vertexShader && fragmentShader && program)) {
    if (vertexShader) {
      gl.deleteShader(vertexShader)
    }
    if (fragmentShader) {
      gl.deleteShader(fragmentShader)
    }
    if (program) {
      gl.deleteProgram(program)
    }
    return () => undefined
  }

  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.bindAttribLocation(program, 0, 'aPosition')
  gl.bindAttribLocation(program, 1, 'aUv')
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return () => undefined
  }

  const buffer = gl.createBuffer()
  const texture = gl.createTexture()
  if (!(buffer && texture)) {
    if (buffer) {
      gl.deleteBuffer(buffer)
    }
    if (texture) {
      gl.deleteTexture(texture)
    }
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return () => undefined
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]), gl.STATIC_DRAW)

  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, NOISE_SIZE, NOISE_SIZE, 0, gl.RGBA, gl.UNSIGNED_BYTE, createNoiseData())

  const warpScrollLocation = gl.getUniformLocation(program, 'uWarpScroll')
  const layerScroll1Location = gl.getUniformLocation(program, 'uLayerScroll1')
  const layerScroll2Location = gl.getUniformLocation(program, 'uLayerScroll2')
  const layerScroll3Location = gl.getUniformLocation(program, 'uLayerScroll3')
  const pulseLocation = gl.getUniformLocation(program, 'uPulse')
  const resolutionLocation = gl.getUniformLocation(program, 'uResolution')

  bindProgram(program)
  gl.enableVertexAttribArray(0)
  gl.enableVertexAttribArray(1)
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0)
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8)
  gl.enable(gl.BLEND)
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
  gl.uniform1i(gl.getUniformLocation(program, 'uNoise'), 0)

  let disposed = false
  let frame = 0
  let elapsed = 0
  let warpX = 0
  let warpY = 0
  let layer1X = 0
  let layer1Y = 0
  let layer2X = 0
  let layer2Y = 0
  let layer3X = 0
  let layer3Y = 0
  let lastNow = performance.now()
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  let reducedMotion = motionQuery.matches

  const draw = (now: number) => {
    if (disposed || gl.isContextLost()) {
      return
    }
    const cssWidth = canvas.clientWidth
    const cssHeight = canvas.clientHeight
    if (cssWidth === 0 || cssHeight === 0) {
      return
    }

    const next = backingSize(cssWidth, cssHeight)
    if (canvas.width !== next.width || canvas.height !== next.height) {
      canvas.width = next.width
      canvas.height = next.height
      gl.viewport(0, 0, next.width, next.height)
      bindProgram(program)
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
      gl.enableVertexAttribArray(0)
      gl.enableVertexAttribArray(1)
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0)
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8)
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    }

    const delta = Math.min(MAX_FRAME_SECONDS, Math.max(0, (now - lastNow) / 1000))
    lastNow = now
    if (!reducedMotion) {
      elapsed += delta
      warpX = advanceScroll(warpX, delta, WARP_SPEED_X)
      warpY = advanceScroll(warpY, delta, WARP_SPEED_Y)
      layer1X = advanceScroll(layer1X, delta, LAYER1_SPEED_X)
      layer1Y = advanceScroll(layer1Y, delta, LAYER1_SPEED_Y)
      layer2X = advanceScroll(layer2X, delta, LAYER2_SPEED_X)
      layer2Y = advanceScroll(layer2Y, delta, LAYER2_SPEED_Y)
      layer3X = advanceScroll(layer3X, delta, LAYER3_SPEED_X)
      layer3Y = advanceScroll(layer3Y, delta, LAYER3_SPEED_Y)
    }

    gl.uniform2f(warpScrollLocation, warpX, warpY)
    gl.uniform2f(layerScroll1Location, layer1X, layer1Y)
    gl.uniform2f(layerScroll2Location, layer2X, layer2Y)
    gl.uniform2f(layerScroll3Location, layer3X, layer3Y)
    gl.uniform1f(pulseLocation, 0.9 + 0.1 * Math.sin(elapsed * 0.35))
    gl.uniform2f(resolutionLocation, canvas.width, canvas.height)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }

  const loop = (now: number) => {
    if (disposed || reducedMotion || document.hidden) {
      return
    }
    frame = requestAnimationFrame(loop)
    if (now - lastNow < FRAME_INTERVAL_MS) {
      return
    }
    draw(now)
  }

  const start = () => {
    if (disposed || reducedMotion || document.hidden) {
      return
    }
    cancelAnimationFrame(frame)
    lastNow = performance.now()
    frame = requestAnimationFrame(loop)
  }

  const onVisibility = () => {
    if (document.hidden) {
      cancelAnimationFrame(frame)
      return
    }
    draw(performance.now())
    start()
  }

  const onMotion = () => {
    reducedMotion = motionQuery.matches
    if (reducedMotion) {
      cancelAnimationFrame(frame)
      draw(performance.now())
      return
    }
    start()
  }

  const resizeObserver = new ResizeObserver(() => {
    draw(performance.now())
  })
  resizeObserver.observe(canvas)
  document.addEventListener('visibilitychange', onVisibility)
  motionQuery.addEventListener('change', onMotion)
  draw(performance.now())
  start()

  return () => {
    disposed = true
    cancelAnimationFrame(frame)
    resizeObserver.disconnect()
    document.removeEventListener('visibilitychange', onVisibility)
    motionQuery.removeEventListener('change', onMotion)
    gl.deleteBuffer(buffer)
    gl.deleteTexture(texture)
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
  }
}
